import Box from '@mui/material/Box';
import type { FormEvent } from 'react';
import { useCallback, useState } from 'react';

import { AddLicenceDialog } from '../../add-licence';
import LicencePickerView, { type LicencePickerViewProps } from './licence-picker.view';

export interface LicencePickerFieldProps extends Omit<LicencePickerViewProps, 'onCreateRequested'> {
  /** A licence was created from the picker; the host makes it the pick. */
  onLicenceCreated: (licenceId: string) => void;
}

/** React bubbles a portalled dialog's events through its host tree; see below. */
const stopSubmit = (event: FormEvent) => event.stopPropagation();

/**
 * The picker plus its way out (#593): "Create a new licence" opens the Licences
 * page's own create dialog, stacked and centred over Add user, without its
 * Customer Success Manager field. Cancelling it lands back on the Add user form
 * as it was; creating hands the new id to the host, which selects it.
 */
export default function LicencePickerField({
  onLicenceCreated,
  ...pickerProps
}: LicencePickerFieldProps) {
  const [creating, setCreating] = useState(false);
  const openCreate = useCallback(() => setCreating(true), []);
  const closeCreate = useCallback(() => setCreating(false), []);

  return (
    <>
      <LicencePickerView {...pickerProps} onCreateRequested={openCreate} />
      {/* The dialog is portalled out of the Add user <form> in the DOM but not in
          React's tree, where its submit would bubble on and submit that form too. */}
      <Box component="span" onSubmit={stopSubmit} sx={{ display: 'contents' }}>
        <AddLicenceDialog open={creating} onClose={closeCreate} onCreated={onLicenceCreated} />
      </Box>
    </>
  );
}
